import { Component, OnInit } from "@angular/core";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { finalize } from "rxjs";
import { LOCAL_STORAGE_KEYS, url } from "src/app/global/constant";
import { AuthService } from "src/app/services/auth.service";
import { CampaignService } from "src/app/services/campaign.service";
import { CompanyService } from "src/app/services/company.service";
import { MasterService } from "src/app/services/master.service";
import { SharedService } from "src/app/services/shared.service";
import { UserService } from "src/app/services/user.service";

@Component({
  selector: "app-onboarding",
  templateUrl: "./onboarding.component.html",
  styleUrls: ["./onboarding.component.scss"],
  standalone: false,
})
export class OnboardingComponent implements OnInit {
  // Steps metadata
  steps = [
    { label: "User Details" },
    { label: "Company Setup" },
    { label: "Knowledge Base" },
    { label: "Invite Team" },
    { label: "Create Campaign" },
  ];

  // State
  currentStep = 1;
  isFormSubmitted = false;

  onBoardingForm: FormGroup;
  invitedEmails: string[] = [];
  selectedProfileImageFile: File | null = null;
  profileImagePreviewUrl: string | null = null;
  selectedCompanyLogoFile: File | null = null;
  companyLogoPreviewUrl: string | null = null;

  loginUserDetails: any = null;
  targetAudienceList: any = [];

  // Step 3: Brand Guidelines files
  brandGuidelinesFiles: File[] = [];
  isDragOver = false;

  // Loading states for each step
  isLoadingStep = false;

  constructor(
    private fb: FormBuilder,
    private sharedService: SharedService,
    private authService: AuthService,
    private userService: UserService,
    private masterService: MasterService,
    private campaignService: CampaignService,
    private companyService: CompanyService,
  ) {
    this.onBoardingForm = this.fb.group({
      // Step 1
      profileImage: [null],
      firstName: ["", [Validators.required]],
      lastName: ["", [Validators.required]],
      // userRole: [""],

      // Step 2
      companyLogo: [null],
      companyName: ["", [Validators.required]],
      website: [""],
      industry: [""],
      // role: ["", [Validators.required]],
      companySize: ["", [Validators.required]],
      // Step 3
      targetAudience: [[]],
      // Step 4
      inviteEmail: ["", [Validators.email]],
      // Step 5
      campaignName: ["", Validators.required],
      campaignDescription: [""],
      startDate: [null],
      endDate: [null],
    });
  }

  industryList: any[] = [];
  roleList: any[] = [];
  companySizeList: any[] = [
    { id: "just-me", name: "Just me" },
    { id: "2-10", name: "2-10 people" },
    { id: "11-50", name: "11-50 people" },
    { id: "51-200", name: "51-200 people" },
    { id: "200+", name: "200+ people" },
  ];

  ngOnInit(): void {
    this.loadUserDetailsFromLocalStorage();
    this.getIndustryList();
    this.getRoleList();
    this.getTargetAudience();
  }

  loadUserDetailsFromLocalStorage(): void {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.loginUserDetails);
    if (!raw) {
      this.cancelOnboarding();
      return;
    }
    try {
      this.loginUserDetails = JSON.parse(raw);

      this.onBoardingForm.patchValue({
        firstName: this.loginUserDetails.firstname || "",
        lastName: this.loginUserDetails.lastname || "",
      });

      if (this.loginUserDetails.profile) {
        this.profileImagePreviewUrl = this.loginUserDetails.profile;
      }
    } catch (error) {
      console.error("Error parsing user details from localStorage:", error);
      this.cancelOnboarding();
    }
  }

  cancelOnboarding(): void {
    this.sharedService.logout();
  }

  getControl(name: string): FormControl {
    return this.onBoardingForm.get(name) as FormControl;
  }

  getIndustryList() {
    this.sharedService.showSpinner();
    this.masterService
      .getIndustryList()
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe((res: any) => {
        if (res && res.statusCode == 200) {
          this.industryList = res.data;
        }
      });
  }

  getRoleList() {
    this.sharedService.showSpinner();
    this.masterService
      .getRoleList()
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe((res: any) => {
        if (res && res.statusCode == 200) {
          this.roleList = res.data;
        }
      });
  }

  nextStep(): void {
    if (this.currentStep === 1) {
      this.saveUserDetails();
    } else if (this.currentStep === 2) {
      this.onCompanyDetailsSave();
    } else if (this.currentStep === 3) {
      this.saveKnowledgeBase();
    } else if (this.currentStep === 4) {
      this.saveInvitedEmails();
    } else if (this.currentStep === 5) {
      //this.saveCampaignDetails();
    } else {
      if (this.currentStep < this.steps.length) {
        this.currentStep += 1;
      }
    }
  }

  skipStep(): void {
    this.currentStep += 1;
  }

  saveUserDetails() {
    const formData = new FormData();
    formData.append("file", this.selectedProfileImageFile);
    formData.append("firstname", this.onBoardingForm.value.firstName);
    formData.append("lastname", this.onBoardingForm.value.lastName);

    this.isLoadingStep = true;
    this.userService
      .onBasicDetailUpdate(formData)
      .pipe(finalize(() => (this.isLoadingStep = false)))
      .subscribe({
        next: (res: any) => {
          if ((res && res.statusCode == 200) || res.statusCode == 201) {
            this.sharedService.successToastNotification(res.message);

            this.loginUserDetails.firstname = res?.data?.firstname;
            this.loginUserDetails.lastname = res?.data?.lastname;
            this.loginUserDetails.profile = res?.data?.profile || null;
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.loginUserDetails,
              JSON.stringify(this.loginUserDetails),
            );

            this.currentStep += 1;
          }
        },
        error: () => {
          this.isLoadingStep = false;
        },
      });
  }

  onCompanyDetailsSave() {
    const formData = new FormData();
    formData.append("file", this.selectedCompanyLogoFile);
    formData.append("name", this.onBoardingForm.value.companyName);
    formData.append("website", this.onBoardingForm.value.website || "");
    formData.append("size", this.onBoardingForm.value.companySize || "");
    formData.append("industry", this.onBoardingForm.value.industry || "");
    // formData.append("role", this.onBoardingForm.value.role || "");
    formData.append("plan_type", this.onBoardingForm.value.plan || "");
    formData.append(
      "slug",
      this.onBoardingForm.value.companyName?.toLowerCase().replace(/ /g, "-") ||
        "",
    );
    formData.append(
      "user_id",
      JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.loginUserDetails))?.id,
    );

    this.isLoadingStep = true;
    this.authService
      .onUserOnboarding(formData)
      .pipe(finalize(() => (this.isLoadingStep = false)))
      .subscribe({
        next: (res: any) => {
          if ((res && res.statusCode == 200) || res.statusCode == 201) {
            this.sharedService.successToastNotification(res.message);
            this.loginUserDetails.companies.push(res?.data || null);
            localStorage.setItem(
              LOCAL_STORAGE_KEYS.loginUserDetails,
              JSON.stringify(this.loginUserDetails),
            );
            this.currentStep++;
          }
        },
        error: () => {
          this.isLoadingStep = false;
        },
      });
  }

  getTargetAudience() {
    this.sharedService.showSpinner();
    this.masterService
      .getTargetAudience()
      .pipe(finalize(() => this.sharedService.hideSpinner()))
      .subscribe((res: any) => {
        if (res && res.statusCode == 200) {
          this.targetAudienceList = res.data;
        }
      });
  }

  skipToHome(): void {
    this.sharedService.gotopage(url.dashboard);
  }

  addInvite(): void {
    const email = (this.onBoardingForm.get("inviteEmail")?.value || "").trim();
    if (email && email.includes("@")) {
      if (!this.invitedEmails.includes(email)) {
        this.invitedEmails.push(email);
      }
      this.onBoardingForm.get("inviteEmail")?.setValue("");
    } else {
      this.isFormSubmitted = true;
      this.sharedService.errorToastNotification("Please enter a valid email");
    }
  }

  removeInvite(email: string): void {
    this.invitedEmails = this.invitedEmails.filter((e) => e !== email);
  }

  createCampaign(): void {
    this.isFormSubmitted = true;
    if (this.onBoardingForm.invalid) {
      return;
    }
    // name, description, start_time, end_time, company_id, members
    const payload = {
      name: this.onBoardingForm.get("campaignName")?.value,
      description: this.onBoardingForm.get("campaignDescription")?.value,
      start_time: this.onBoardingForm.get("campaignStartTime")?.value,
      end_time: this.onBoardingForm.get("campaignEndTime")?.value,
      company_id: this.loginUserDetails?.companies[0]?.id,
      members: this.invitedEmails,
    };

    this.isLoadingStep = true;
    this.campaignService
      .onCreateCampaign(payload)
      .pipe(finalize(() => (this.isLoadingStep = false)))
      .subscribe({
        next: (res: any) => {
          if ((res && res.statusCode == 200) || res.statusCode == 201) {
            this.sharedService.successToastNotification(res.message);
            this.skipToHome();
          }
        },
        error: () => {
          this.isLoadingStep = false;
        },
      });
  }

  onUploadClick(type: "userPhoto" | "companyLogo" | "brandGuidelines"): void {
    if (type === "brandGuidelines") {
      const fileInput = document.getElementById(
        "brandGuidelinesFileInput",
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.click();
      }
    }
  }

  onBrandGuidelinesFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input?.files || input.files.length === 0) {
      return;
    }
    this.handleFiles(Array.from(input.files));
    // Reset input to allow selecting the same file again
    input.value = "";
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    if (!event.dataTransfer?.files || event.dataTransfer.files.length === 0) {
      return;
    }

    this.handleFiles(Array.from(event.dataTransfer.files));
  }

  handleFiles(files: File[]): void {
    const maxSizeBytes = 10 * 1024 * 1024; // 10MB
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ]; // PDF, DOC, DOCX

    files.forEach((file) => {
      // Validate file type
      if (!validTypes.includes(file.type)) {
        this.sharedService.errorToastNotification(
          `${file.name}: Please select a PDF, DOC, or DOCX file`,
        );
        return;
      }

      // Validate file size
      if (file.size > maxSizeBytes) {
        this.sharedService.errorToastNotification(
          `${file.name}: File size must be under 10MB`,
        );
        return;
      }

      // Check if file already exists
      const fileExists = this.brandGuidelinesFiles.some(
        (f) => f.name === file.name && f.size === file.size,
      );

      if (!fileExists) {
        this.brandGuidelinesFiles.push(file);
      } else {
        this.sharedService.errorToastNotification(
          `${file.name}: File already added`,
        );
      }
    });
  }

  removeBrandGuidelinesFile(index: number): void {
    this.brandGuidelinesFiles.splice(index, 1);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }

  saveKnowledgeBase(): void {
    const formData = new FormData();

    // Append all brand guidelines files
    // this.brandGuidelinesFiles.forEach((file, index) => {
    //   formData.append(`file_${index}`, this.brandGuidelinesFiles[index]);
    // });
    formData.append(`file`, this.brandGuidelinesFiles[0]);

    // Append target audience if selected
    if (this.onBoardingForm.value.targetAudience) {
      formData.append("audience_id", this.onBoardingForm.value.targetAudience);
    }

    // Append user/company ID if needed
    if (this.loginUserDetails?.id) {
      formData.append("company_id", this.loginUserDetails.companies[0]?.id);
    }

    this.isLoadingStep = true;
    this.companyService
      .saveKnowledgeBase(formData)
      .pipe(finalize(() => (this.isLoadingStep = false)))
      .subscribe({
        next: (res: any) => {
          if ((res && res.statusCode == 200) || res.statusCode == 201) {
            this.sharedService.successToastNotification(
              res.message || "Knowledge base saved successfully",
            );
            this.currentStep += 1;
          }
        },
        error: (error) => {
          this.sharedService.errorToastNotification(
            error?.error?.message || "Failed to save knowledge base",
          );
          this.isLoadingStep = false;
        },
      });
  }

  onProfileImageSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input?.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSizeBytes = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      this.sharedService.errorToastNotification(
        "Please select a JPG, PNG, or WEBP image",
      );
      return;
    }

    if (file.size > maxSizeBytes) {
      this.sharedService.errorToastNotification("Image size must be under 5MB");
      return;
    }

    this.selectedProfileImageFile = file;
    this.onBoardingForm.patchValue({ profileImage: file });

    // Preview
    const reader = new FileReader();
    reader.onload = () => {
      this.profileImagePreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeProfileImage(event: Event, input: HTMLInputElement): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedProfileImageFile = null;
    this.profileImagePreviewUrl = null;
    this.onBoardingForm.patchValue({ profileImage: null });
    input.value = "";
  }

  onCompanyLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input?.files || input.files.length === 0) {
      return;
    }
    const file = input.files[0];
    const validTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSizeBytes = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      this.sharedService.errorToastNotification(
        "Please select a JPG, PNG, or WEBP image",
      );
      return;
    }

    if (file.size > maxSizeBytes) {
      this.sharedService.errorToastNotification("Image size must be under 5MB");
      return;
    }

    this.selectedCompanyLogoFile = file;
    this.onBoardingForm.patchValue({ companyLogo: file });

    // Preview
    const reader = new FileReader();
    reader.onload = () => {
      this.companyLogoPreviewUrl = reader.result as string;
    };
    reader.readAsDataURL(file);
  }

  removeCompanyLogo(event: Event, input: HTMLInputElement): void {
    event.preventDefault();
    event.stopPropagation();
    this.selectedCompanyLogoFile = null;
    this.companyLogoPreviewUrl = null;
    this.onBoardingForm.patchValue({ companyLogo: null });
    input.value = "";
  }

  saveInvitedEmails() {
    const payload = {
      email: this.invitedEmails,
      company_id: this.loginUserDetails?.companies[0]?.id,
    };
    this.isLoadingStep = true;
    this.userService
      .onInviteUserV2(payload)
      .pipe(finalize(() => (this.isLoadingStep = false)))
      .subscribe({
        next: (res: any) => {
          if ((res && res.statusCode == 200) || res.statusCode == 201) {
            this.sharedService.successToastNotification(res.message);
            this.currentStep += 1;
          }
        },
        error: () => {
          this.isLoadingStep = false;
        },
      });
  }
}
